/*
 * Copyright (c) 2022. World Wide Generation Ltd
 */

import { naturalSort } from '.';
import { ScopeQuestionOptionalValue, UtrSortingConfig } from '../types/survey';
import { SurveyType } from '../types/surveyCommon';

interface SurveyEffectiveDate {
  effectiveDate: string
  created?: string;
  type?: SurveyType;
}

export const sortSurveysDesc = (a: SurveyEffectiveDate, b: SurveyEffectiveDate) => {
  if (a.effectiveDate < b.effectiveDate) {
    return 1;
  }

  if (a.effectiveDate > b.effectiveDate) {
    return -1;
  }

  // Fallback to created
   if (a.created && b.created) {
     if (a.created < b.created) {
       return 1;
     }

     if (a.created > b.created) {
       return -1;
     }
   }

  // Fallback to created
  if (a.type && b.type) {
    if (a.type === 'aggregation' && b.type === 'default') {
      return -1;
    }

    if (a.type === 'default' && b.type === 'aggregation') {
      return 1;
    }
  }

  return 0
}

export function sortItemsByTypeCode<T extends ScopeQuestionOptionalValue = ScopeQuestionOptionalValue>(
  cards: T[],
  alternativeCode?: string
): T[] {
  const getTypeCode = (q: T, alternativeCode?: string) => {
    return q.universalTracker.getTypeCode(alternativeCode) || '';
  };
  return cards.slice().sort((a, b) => (
    naturalSort(getTypeCode(a, alternativeCode), getTypeCode(b, alternativeCode))
    ||
    naturalSort(a.name, b.name))
  );
}

// Iterative function to find the matching subgroup that contains our code
export const findMatchingConfig = (config: UtrSortingConfig | undefined, code: string): UtrSortingConfig | undefined => {
  if (!config) {
    return undefined;
  }

  // Use a stack to traverse the tree iteratively
  const configs: UtrSortingConfig[] = [config];

  while (configs.length > 0) {
    const currentConfig = configs.pop();

    if (currentConfig?.code === code) {
      return currentConfig;
    }

    // Add subgroups to the stack for processing (in reverse order to maintain left-to-right traversal)
    const subgroups = currentConfig?.subgroups;
    if (subgroups) {
      for (let i = subgroups.length - 1; i >= 0; i--) {
        configs.push(subgroups[i]);
      }
    }
  }

  return undefined;
};

export const sortItemsByUtrSortingOrder = <T extends ScopeQuestionOptionalValue = ScopeQuestionOptionalValue>(
  questions: T[],
  order: string[],
) => {
  return questions.slice().sort((a, b) => {
    const aCode = a.universalTracker.getCode() || '';
    const bCode = b.universalTracker.getCode() || '';

    // Get indices from the order array
    const aIndex = order.indexOf(aCode);
    const bIndex = order.indexOf(bCode);

    // If both codes are not found in order, maintain original order
    if (aIndex === -1 && bIndex === -1) {
      return 0;
    }

    // If one code is not found, put it at the end
    if (aIndex === -1) {
      return 1;
    }

    if (bIndex === -1) {
      return -1;
    }

    // Sort based on order array indices
    return aIndex - bIndex;
  });
};
